import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgePercent, Boxes, Clapperboard, Landmark, LogOut, Package, RefreshCw, Star, Target } from "lucide-react";
import { ReviewsAdmin } from "../components/admin/ReviewsAdmin";
import { usePrivatePage } from "../lib/seo";
import { Logo } from "../components/Header";
import { AdminLogin } from "../components/admin/AdminLogin";
import { OrderCard } from "../components/admin/OrderCard";
import { MarketingClips } from "../components/admin/MarketingClips";
import { Vouchers } from "../components/admin/Vouchers";
import { CaptionWriter } from "../components/admin/CaptionWriter";
import { PhotoCleanup } from "../components/admin/PhotoCleanup";
import { Payouts } from "../components/admin/Payouts";
import { Batches } from "../components/admin/Batches";
import { Coupons } from "../components/admin/Coupons";
import { StbAdmin } from "../components/admin/StbAdmin";
import { adminApi, errMsg, getAdminToken, setAdminToken } from "../lib/api";
import { eur } from "../lib/shield";

const TABS = [["orders", "Orders", Package], ["batches", "Club batches", Boxes], ["payouts", "Payouts", Landmark], ["codes", "Discount codes", BadgePercent], ["marketing", "Marketing", Clapperboard], ["stb", "Fundraising games", Target], ["reviews", "Reviews", Star]];
const initialTab = () => { const h = window.location.hash.slice(1); return TABS.some(([id]) => id === h) ? h : "orders"; };

const FILTERS = [["paid_all", "Paid (all stages)"], ["paid", "New – paid"], ["printing", "Printing"], ["dispatched", "Dispatched"], ["all", "Everything"]];

export default function Admin() {
  usePrivatePage("Admin");
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [filter, setFilter] = useState("paid_all");
  const [orders, setOrders] = useState(null);
  const [tab, setTabState] = useState(initialTab);
  const setTab = (id) => { setTabState(id); window.history.replaceState(null, "", `#${id}`); };

  const load = useCallback(() => {
    setOrders(null);
    adminApi().get(`/admin/orders?status=${filter}`).then((r) => setOrders(r.data)).catch((e) => {
      if (e?.response?.status === 401) { setAdminToken(null); setAuthed(false); } else toast.error(errMsg(e));
      setOrders([]);
    });
  }, [filter]);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  if (!authed) return <AdminLogin onDone={() => setAuthed(true)} />;
  const paid = (orders || []).filter((o) => ["paid", "printing", "dispatched"].includes(o.status));
  const shields = paid.reduce((n, o) => n + o.items.reduce((m, i) => m + i.quantity, 0), 0);
  const revenue = paid.reduce((n, o) => n + o.total, 0);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <button type="button" onClick={() => { setAdminToken(null); setAuthed(false); }} data-testid="admin-logout-button"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="stripe-rule" />
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-10">
        <div className="eyebrow">Admin</div>
        <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">Order dashboard</h1>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-xl">
          {[["Orders", orders?.length ?? "–", "orders"], ["Paid shields", shields, "shields"], ["Paid revenue", eur(revenue), "revenue"]].map(([l, v, id]) => (
            <div key={l} className="border-2 border-slate-900 bg-white p-3 shadow-[3px_3px_0_0_#0f172a]">
              <div className="eyebrow">{l}</div><div className="font-cond text-2xl font-bold" data-testid={`admin-stat-${id}`}>{v}</div>
            </div>
          ))}
        </div>
        <nav className="sticky top-16 z-30 -mx-4 mt-8 flex gap-1 overflow-x-auto border-b-2 border-slate-900 bg-slate-50/95 px-4 backdrop-blur sm:mx-0 sm:px-0" role="tablist" data-testid="admin-tabs">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`-mb-0.5 flex shrink-0 items-center gap-1.5 border-2 border-b-0 px-3 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors sm:px-4 sm:text-sm ${tab === id ? "border-slate-900 bg-white text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              <Icon className="h-4 w-4" /> {label}</button>
          ))}
        </nav>
        {tab === "batches" && <Batches />}
        {tab === "stb" && <StbAdmin />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "payouts" && <Payouts />}
        {tab === "codes" && <><Coupons /><Vouchers /></>}
        {tab === "marketing" && <><CaptionWriter /><MarketingClips /></>}
        {tab === "orders" && <>
        <PhotoCleanup />
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-filters">
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`admin-filter-${id}`}
              className={`border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${filter === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-900"}`}>{label}</button>
          ))}
          <button type="button" onClick={load} className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="admin-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</button>
        </div>
        <div className="mt-5 space-y-4" data-testid="admin-orders-list">
          {orders === null && <p className="text-slate-500">Loading orders…</p>}
          {orders?.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="admin-orders-empty">No orders here yet.</p>}
          {orders?.map((o) => <OrderCard key={o.id} order={o} onChanged={load} />)}
        </div>
        </>}
      </main>
    </div>
  );
}
